<template>
    <div class="card">
        <h6>Search Criteria:</h6>
        <Accordion>
            <AccordionTab header="Use as few or as many as you wish, partial matches are acceptable.">
                <div class="p-fluid formgrid grid">
                    <div class="field col-6 md:col-3">
                        <IncidentsSelectionPanel v-model="debriefCriteria.incident_id" @incident-selected="incidentsSelected">Incidents</IncidentsSelectionPanel>
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-multi-select v-model="debriefCriteria.category" label="Incident Category" :options="getAllIncidentCategories()" optionLabel="name" optionValue="id" :squaredChips="true" :filter="false" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <custom-vue-tree-select :key="treeSelectKey" v-model="debriefCriteria.incident_type_id" label="Incident Type" :optionsTree="incidentTypesVueTree" :showLabel="true" :valueConsist="'ALL'" :multiple="true" :limit="2" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-selection-input v-model="debriefCriteria.learning" label="Learning Identified" :radio-type="true" :options="getCriteriaYesNoOptions()" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-multi-select v-model="debriefCriteria.appliance_id" label="Call Sign" :options="appliances" optionLabel="name" optionValue="id" displayType="comma" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <UsersSelectionPanel v-model="debriefCriteria.author_id">Authors: </UsersSelectionPanel>
                    </div>
                    <div class="field col-6 md:col-3">
                        <custom-vue-tree-select :key="treeSelectKey" v-model="debriefCriteria.theme_id" label="Themes" :options-tree="themeVueTree" :showLabel="true" :valueConsist="'ALL'" :multiple="true" :limit="2" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <custom-vue-tree-select :key="treeSelectKey" v-model="debriefCriteria.source_id" label="Source" :optionsTree="sourceVueTree" :showLabel="true" :valueConsist="'ALL'" :multiple="true" :limit="2" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-multi-select v-model="debriefCriteria.learning_outcome_type" label="Outcome Types" :options="getAllOutcomeTypes()" optionLabel="name" optionValue="id" displayType="comma" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-calendar-input v-model="debriefCriteria.created_at_from" label="Debrief Created Date From" dateFormat="dd-mm-yy" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-calendar-input v-model="debriefCriteria.created_at_to" label="Debrief Created Date To" dateFormat="dd-mm-yy" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-calendar-input v-model="debriefCriteria.incident_date_from" label="Incident Date From" dateFormat="dd-mm-yy" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <base-calendar-input v-model="debriefCriteria.incident_date_to" label="Incident Date To" dateFormat="dd-mm-yy" />
                    </div>
                     <div class="field col-6 md:col-3">
                        <label for="debriefId">Debrief ID:</label>
                        <InputText id="debriefId" type="text" v-model="debriefCriteria.debriefId" />
                    </div>
                    <div class="mt-4 formgrid grid col-8">
                        <div class="field col-6 md:col-3">
                            <Button type="button" label="Search" icon="pi pi-search" @click="applyCriteria" />
                        </div>
                        <div class="field col-6 md:col-3">
                            <Button type="button" label="Clear" icon="pi pi-trash" @click="clearCriteria" />
                        </div>
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </div>
</template>
<script setup>
import UsersSelectionPanel from '@/modules/user/components/UsersSelectionPanel';

/*
Please note:
The component VueTreeSelect is not able to react to v-model changes: if it changes no internal changes are fired.
Change the :key to force Vue to consider it a new component.
E.g.
<custom-vue-tree-select :key="my_key" v-model="my_model" [...] />

In this case, is treeSelectKey updated by clearCriteria()
 */

import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import NProgress from 'nprogress';

import { applianceStore, incidentStore, reportStore, sourceStore, themeStore } from '@/store/global-stores';
import { getCriteriaYesNoOptions } from '@/constants';
import { getAllOutcomeTypes } from '../../../learning-outcome/constants';
import { getAllIncidentCategories } from '../../../incident/constants';
import { showToast } from '@/utilities/toast-handler';
import IncidentsSelectionPanel from '@/modules/user/components/IncidentsSelectionPanel.vue';

const { debriefCriteria } = storeToRefs(reportStore);
const { incidentTypesVueTree } = storeToRefs(incidentStore);
const { appliances } = storeToRefs(applianceStore);
const { themeVueTree } = storeToRefs(themeStore);
const { sourceVueTree } = storeToRefs(sourceStore);

const treeSelectKey = ref(0);

const emit = defineEmits(['set-criteria']);

onMounted(async () => {
    loadServices();
});

const loadServices = async () => {
    NProgress.start();
    await incidentStore.fetchIncidentTypes();
    await applianceStore.fetchAllAppliances();
    await themeStore.fetchAllThemes();
    await sourceStore.fetchAllSources();
    NProgress.done();
};

const incidentsSelected = (incidentIds) => {
    /*
     * The debrief search criteria is applied here, directly, because wasn't absolutely possible to discover where
     * the debriefCriteria store is updated with the criteria added by user.
     */
    debriefCriteria.value.incident_id = incidentIds;
};

const applyCriteria = async () => {
    // Need to load chart and table both
    emit('set-criteria');
};
const clearCriteria = () => {
    treeSelectKey.value++
    reportStore.initDebriefCriteria();
    applyCriteria();
    showToast({ severity: 'info', summary: 'Filters Cleared' }, 'All Criteria filters have been cleared');
};
</script>
